import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import Select from 'react-select'
import '../Home/Home.scss'
import '../HomeDetails/HomeDetails.scss'
import './Sales.scss'
import MobileHeader from '../Common/Header/MobileHeader'
import Header from '../Common/Header/Header'
import Config from '../../config/config'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import SideMenu from '../Common/SideMenu/SideMenu'
import BarcodeIcon from '../../assets/BarcodeIcon'
import BarcodeScanner from '../../components/ZXingScanner/BarcodeScanner'
import TrashIcon from '../../assets/TrashIcon'
import PopupCard from '../Common/PopupCard/PopupCard'
import {
  createSalesHelper,
  updateSalesHelper,
  getSalesOrderHelper,
  getCustomerHelper,
  searchCustomersHelper,
  getProductDetailsByBarcodeHelper,
  getBranchProductsHelper,
  cancelSalesHelper,
  addCustomerHelper,
  confirmOnlineSalesOrderHelper,
  updatePrescriptionOrderHelper,
} from '../../helper/home-helper'
import { getAllPOSHelper } from '../../helper/pos-details-helper'
import { getAllMFSHelper } from '../../helper/mfs-details-helper'
import {
  getProductSearchSuggestionsHelper,
  getProductDetailByIdHelper,
} from '../../helper/personal-details-helper'
import {
  createSalesAlertAction,
  holdSalesAlertAction,
  updateSalesAlertAction,
  activeHomeDetailNavigation,
} from '../../modules/actions/action'
import {
  CUSTOMER_FIELDS,
  PRODUCT_DETAIL_FIELDS,
  PAYMENT_DETAILS_FIELDS,
  PAYMENT_MODE_OPTIONS,
  PERMISSION_FEATURE,
  PERMISSION_TYPE,
  HOME_NAVIGATION_ITEMS,
  HOME_CONST,
  SALES_FLOW,
  SALES_TYPE,
} from '../../common/constant/page-constant'
import { SIDEBAR_CONST } from '../../common/constant/common-constant'
import { formatDate } from '../../common/utils/appUtils'
import { saveDraft, loadDraft, clearDraft } from '../../common/utils/storageUtils'
import DRAFT_KEYS from '../../draft/Draft'
import { handlePrintInvoice } from './printHandler'

const SalesForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const isMobile = useCheckMobileScreen()
  const loginData = useSelector((state) => state.loginDataReducer?.loginData)
  const permissions = loginData?.permissions

  const isEditMode = id && id !== 'new'
  
  // Use different draft keys for new mode vs edit mode (per sales order ID)
  // Using SALES_FORM key to avoid conflict with Sales.js drawer component
  const DRAFT_KEY = isEditMode 
    ? `${DRAFT_KEYS.SALES_FORM}_edit_${id}` 
    : DRAFT_KEYS.SALES_FORM
  
  // Track if initial data load is complete (for edit mode) or draft restore is complete (for new mode)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  // Section 1: Customer Details
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerCountryCode, setCustomerCountryCode] = useState('+880')
  const [customerName, setCustomerName] = useState('')
  const [customerAddress, setCustomerAddress] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [customerSuggestions, setCustomerSuggestions] = useState([])
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)

  // Section 2: Product Details
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [productBarcode, setProductBarcode] = useState('')
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [quantity, setQuantity] = useState('')
  const [costPerUnit, setCostPerUnit] = useState(0)
  const [ratePerUnit, setRatePerUnit] = useState(0)
  const [discountPercentage, setDiscountPercentage] = useState(0)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [vatPercentage, setVatPercentage] = useState(0)
  const [vatAmount, setVatAmount] = useState(0)
  const [productSearchSuggestions, setProductSearchSuggestions] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [isScannerVisible, setIsScannerVisible] = useState(false)

  // Section 3: Products List
  const [productsInTable, setProductsInTable] = useState([])
  const [selectedTableRowIndex, setSelectedTableRowIndex] = useState(null)

  // Section 4: Bill Summary
  const [totalQty, setTotalQty] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [tableDiscountPercentage, setTableDiscountPercentage] = useState(0)
  const [tableDiscountAmount, setTableDiscountAmount] = useState(0)
  const [subTotal, setSubTotal] = useState(0)
  const [totalVAT, setTotalVAT] = useState(0)
  const [netAmount, setNetAmount] = useState(0)
  const [paidAmount, setPaidAmount] = useState('')
  const [returnAmount, setReturnAmount] = useState(0)

  // Section 5: Payment Details
  const [paymentMode, setPaymentMode] = useState('CASH')
  const [cashAmount, setCashAmount] = useState(0)
  const [cardAmount, setCardAmount] = useState(0)
  const [notes, setNotes] = useState('')

  // POS and MFS data for payment modes
  const [posList, setPosList] = useState([])
  const [mfsList, setMfsList] = useState([])
  const [selectedPOS, setSelectedPOS] = useState(null)
  const [selectedCardType, setSelectedCardType] = useState(null) // SAME_BANK or OTHER_BANK
  const [selectedMFS, setSelectedMFS] = useState(null)
  const [transactionFeePercent, setTransactionFeePercent] = useState(0)
  const [transactionFeeAmount, setTransactionFeeAmount] = useState(0)
  const [totalPayable, setTotalPayable] = useState(0)

  // Card type options
  const CARD_TYPE_OPTIONS = [
    { name: 'SAME_BANK', label: 'Same Bank' },
    { name: 'OTHER_BANK', label: 'Other Bank' }
  ]

  // Print checkbox - default to checked
  const [shouldPrint, setShouldPrint] = useState(true)

  // UI State
  const [loading, setLoading] = useState(false)
  const [serverError, setServerError] = useState(undefined)
  const [isFieldErrorVisible, setIsFieldErrorVisible] = useState(false)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [deleteProductPopup, setDeleteProductPopup] = useState(false)
  const [salesFlow, setSalesFlow] = useState(SALES_FLOW.OFFLINE)
  const [validationError, setValidationError] = useState(null) // For popup validation errors

  // Online order state
  const [isOnlineOrder, setIsOnlineOrder] = useState(false)
  const [onlineOrderStatus, setOnlineOrderStatus] = useState(null)
  const [isPrescriptionOrder, setIsPrescriptionOrder] = useState(false)
  const [prescriptionFiles, setPrescriptionFiles] = useState([])
  const [showConfirmPopup, setShowConfirmPopup] = useState(false)
  const [showCancelOrderPopup, setShowCancelOrderPopup] = useState(false)
  const [selectedPrescriptionImage, setSelectedPrescriptionImage] = useState(null)

  const barcodeInputRef = useRef(null)
  const firstInputRef = useRef(null)
  const productSearchTimerRef = useRef(null)
  const productSearchAbortRef = useRef(null)

  // Load all products on mount and focus first input
  useEffect(() => {
    loadAllProducts()
    loadPOSData()
    loadMFSData()
    // Auto-focus the first input field when page loads
    if (firstInputRef.current) {
      firstInputRef.current.focus()
    }
  }, [])

  // Load POS data
  const loadPOSData = () => {
    getAllPOSHelper()
      .then(res => {
        setPosList(res || [])
      })
      .catch(err => {
        console.log('Error loading POS data: ', err)
        setPosList([])
      })
  }

  // Load MFS data
  const loadMFSData = () => {
    getAllMFSHelper()
      .then(res => {
        setMfsList(res || [])
      })
      .catch(err => {
        console.log('Error loading MFS data: ', err)
        setMfsList([])
      })
  }

  // Load edit data
  useEffect(() => {
    if (isEditMode) {
      loadSalesOrder(id)
    }
  }, [id])

  // Close customer suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-field-wrapper')) {
        setShowCustomerSuggestions(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Helper function to restore draft data
  const restoreDraftData = (draft) => {
    // Section 1: Customer Details
    if (draft.customerPhone) setCustomerPhone(draft.customerPhone)
    if (draft.customerCountryCode) setCustomerCountryCode(draft.customerCountryCode)
    if (draft.customerName) setCustomerName(draft.customerName)
    if (draft.customerAddress) setCustomerAddress(draft.customerAddress)
    if (draft.selectedCustomer) setSelectedCustomer(draft.selectedCustomer)
    // Section 2: Product Details (form fields)
    if (draft.selectedProduct) setSelectedProduct(draft.selectedProduct)
    if (draft.productBarcode) setProductBarcode(draft.productBarcode)
    if (draft.selectedBatch) setSelectedBatch(draft.selectedBatch)
    if (draft.quantity) setQuantity(draft.quantity)
    if (draft.costPerUnit !== undefined) setCostPerUnit(draft.costPerUnit)
    if (draft.ratePerUnit !== undefined) setRatePerUnit(draft.ratePerUnit)
    if (draft.discountPercentage !== undefined) setDiscountPercentage(draft.discountPercentage)
    if (draft.discountAmount !== undefined) setDiscountAmount(draft.discountAmount)
    if (draft.vatPercentage !== undefined) setVatPercentage(draft.vatPercentage)
    if (draft.vatAmount !== undefined) setVatAmount(draft.vatAmount)
    // Section 3: Products Table
    if (draft.productsInTable) setProductsInTable(draft.productsInTable)
    // Section 4: Bill Summary
    if (draft.tableDiscountPercentage !== undefined) setTableDiscountPercentage(draft.tableDiscountPercentage)
    if (draft.tableDiscountAmount !== undefined) setTableDiscountAmount(draft.tableDiscountAmount)
    if (draft.paidAmount) setPaidAmount(draft.paidAmount)
    // Section 5: Payment Details
    if (draft.paymentMode) setPaymentMode(draft.paymentMode)
    if (draft.cashAmount !== undefined) setCashAmount(draft.cashAmount)
    if (draft.cardAmount !== undefined) setCardAmount(draft.cardAmount)
    if (draft.notes) setNotes(draft.notes)
    if (draft.selectedPOS) setSelectedPOS(draft.selectedPOS)
    if (draft.selectedCardType) setSelectedCardType(draft.selectedCardType)
    if (draft.selectedMFS) setSelectedMFS(draft.selectedMFS)
    // Print checkbox
    if (draft.shouldPrint !== undefined) setShouldPrint(draft.shouldPrint)
  }

  // Restore draft on mount (for new mode)
  useEffect(() => {
    if (!isEditMode) {
      const draft = loadDraft(DRAFT_KEY)
      if (draft) {
        restoreDraftData(draft)
      }
      // Mark initial load/restore as complete so draft saving can begin
      setIsInitialLoadComplete(true)
    }
  }, [isEditMode])

  // Persist draft on state change (for both new and edit mode)
  // Only save after initial load/restore is complete to avoid overwriting with empty data
  useEffect(() => {
    if (isInitialLoadComplete) {
      saveDraft(DRAFT_KEY, {
        // Section 1: Customer Details
        customerPhone,
        customerCountryCode,
        customerName,
        customerAddress,
        selectedCustomer,
        // Section 2: Product Details (form fields)
        selectedProduct,
        productBarcode,
        selectedBatch,
        quantity,
        costPerUnit,
        ratePerUnit,
        discountPercentage,
        discountAmount,
        vatPercentage,
        vatAmount,
        // Section 3: Products Table
        productsInTable,
        // Section 4: Bill Summary
        tableDiscountPercentage,
        tableDiscountAmount,
        paidAmount,
        // Section 5: Payment Details
        paymentMode,
        cashAmount,
        cardAmount,
        notes,
        selectedPOS,
        selectedCardType,
        selectedMFS,
        // Print checkbox
        shouldPrint
      })
    }
  }, [
    isEditMode,
    isInitialLoadComplete,
    customerPhone,
    customerCountryCode,
    customerName,
    customerAddress,
    selectedCustomer,
    selectedProduct,
    productBarcode,
    selectedBatch,
    quantity,
    costPerUnit,
    ratePerUnit,
    discountPercentage,
    discountAmount,
    vatPercentage,
    vatAmount,
    productsInTable,
    tableDiscountPercentage,
    tableDiscountAmount,
    paidAmount,
    paymentMode,
    cashAmount,
    cardAmount,
    notes,
    selectedPOS,
    selectedCardType,
    selectedMFS,
    shouldPrint
  ])

  const loadAllProducts = async () => {
    getBranchProductsHelper()
      .then((products) => {
        if (products && Array.isArray(products)) {
          setAllProducts(products)
          setProductSearchSuggestions(products)
        }
      })
      .catch((err) => {
        console.log('Error loading products:', err)
      })
  }

  const loadSalesOrder = async (orderId) => {
    setLoading(true)
    getSalesOrderHelper(orderId)
      .then((order) => {
        // Populate customer details
        setCustomerName(order.customer?.name || '')
        setCustomerPhone(
          order.customer?.mobile_no?.replace(customerCountryCode, '') || ''
        )
        setCustomerAddress(
          order.customer?.shipping_address ||
            order.customer?.billing_address ||
            ''
        )
        setSelectedCustomer(order.customer)

        // Populate products table
        if (order.product_details && order.product_details.length > 0) {
          const products = order.product_details.map((detail, index) => ({
            id: detail.id || `product_${index}`,
            productDetails: detail,
            batchDetails:
              detail.inventory || detail.selected_batch_details || {},
            barcode: detail.inventory?.barcode || '',
            productName: detail[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || '',
            dose: detail[PRODUCT_DETAIL_FIELDS.DOSE] || '',
            category: detail.category || '',
            batchNumber:
              detail.inventory?.[PRODUCT_DETAIL_FIELDS.BATCH_NUMBER] ||
              detail[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.[
                PRODUCT_DETAIL_FIELDS.BATCH_NUMBER
              ] ||
              '',
            expiryDate:
              detail.inventory?.expiry_date ||
              detail[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]
                ?.expiry_date ||
              '',
            quantity: detail[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0,
            costPerUnit:
              detail.inventory?.cost_per_unit ||
              detail[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]
                ?.cost_per_unit ||
              0,
            ratePerUnit: detail[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0,
            discountPercentage: detail.discount_percentage || 0,
            discountAmount: 0,
            vatPercentage: detail.vat_percentage || 0,
            vatAmount: detail.vat || 0,
            totalAmount: detail.final_price || 0,
            rxMode: detail.inventory?.is_prescription_required || detail.is_prescription_required ? 'Rx' : 'OTC',
          }))
          setProductsInTable(products)
        }

        // Populate payment details
        setPaymentMode(order.payment_mode || 'CASH')
        setNotes(order.notes || '')
        setTableDiscountPercentage(order.discount || 0)

        if (order.payment_mode === 'CASH_CARD') {
          setCashAmount(order.amount_cash || 0)
          setCardAmount(order.amount_card || 0)
        }

        setSalesFlow(
          order.type === 'IN_STORE' ? SALES_FLOW.OFFLINE : SALES_FLOW.ONLINE
        )
        
        // Set online order state
        const isOnline = order.type === 'ONLINE'
        setIsOnlineOrder(isOnline)
        setOnlineOrderStatus(order.status)
        
        if (isOnline) {
          // Check if it's a prescription order
          const isPrescription = order.order_via_prescription || 
            order.status === 'PRESCRIPTION_PENDING_ON_STORE'
          setIsPrescriptionOrder(isPrescription)
          
          // Load prescription files if available
          if (order.files && order.files.length > 0) {
            setPrescriptionFiles(order.files)
          }
        }
        
        // After loading from backend, check if there's a draft with unsaved changes
        // The draft has priority because it contains more recent user edits
        const editDraftKey = `${DRAFT_KEYS.SALES_FORM}_edit_${orderId}`
        const draft = loadDraft(editDraftKey)
        if (draft) {
          // Restore draft data (overwrites backend data with user's unsaved changes)
          restoreDraftData(draft)
        }
        
        // Mark initial load as complete so draft saving can begin
        setIsInitialLoadComplete(true)
        setLoading(false)
      })
      .catch((err) => {
        console.log('Error loading sales order:', err)
        setIsInitialLoadComplete(true) // Allow saving even if load fails
        setLoading(false)
      })
  }

  // Customer search by phone
  const handleCustomerPhoneChange = (phone) => {
    setCustomerPhone(phone)
    setSelectedSuggestionIndex(-1)

    if (phone.length >= 3) {
      searchCustomersHelper(phone)
        .then((customers) => {
          if (customers && customers.length > 0) {
            setCustomerSuggestions(customers)
            setShowCustomerSuggestions(true)

            if (customers.length === 1) {
              const customer = customers[0]
              setCustomerName(customer.name || '')
              setCustomerAddress(
                customer.address || customer.shipping_address || ''
              )
              setSelectedCustomer(customer)
            }
          } else {
            setCustomerSuggestions([])
            setShowCustomerSuggestions(false)
          }
        })
        .catch(() => {
          setCustomerSuggestions([])
          setShowCustomerSuggestions(false)
        })
    } else {
      setCustomerSuggestions([])
      setShowCustomerSuggestions(false)
    }
  }

  // Customer search by name
  const handleCustomerNameChange = (name) => {
    setCustomerName(name)
    setSelectedSuggestionIndex(-1)

    if (name.length >= 3) {
      searchCustomersHelper(name)
        .then((customers) => {
          if (customers && customers.length > 0) {
            setCustomerSuggestions(customers)
            setShowCustomerSuggestions(true)

            if (customers.length === 1) {
              const customer = customers[0]
              setCustomerPhone(
                customer.mobile_no?.replace(customerCountryCode, '') || ''
              )
              setCustomerAddress(
                customer.address || customer.shipping_address || ''
              )
              setSelectedCustomer(customer)
            }
          } else {
            setCustomerSuggestions([])
            setShowCustomerSuggestions(false)
          }
        })
        .catch(() => {
          setCustomerSuggestions([])
          setShowCustomerSuggestions(false)
        })
    } else {
      setCustomerSuggestions([])
      setShowCustomerSuggestions(false)
    }
  }

  // Handle keyboard navigation for customer suggestions
  const handleCustomerInputKeyDown = (e) => {
    if (!showCustomerSuggestions || customerSuggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedSuggestionIndex((prevIndex) =>
        prevIndex < customerSuggestions.length - 1 ? prevIndex + 1 : 0
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedSuggestionIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : customerSuggestions.length - 1
      )
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault()
      handleSelectCustomer(customerSuggestions[selectedSuggestionIndex])
    } else if (e.key === 'Escape') {
      setShowCustomerSuggestions(false)
      setSelectedSuggestionIndex(-1)
    }
  }

  const handleSelectCustomer = (customer) => {
    setCustomerName(customer.name || '')
    setCustomerPhone(customer.mobile_no?.replace(customerCountryCode, '') || '')
    setCustomerAddress(customer.address || customer.shipping_address || '')
    setSelectedCustomer(customer)
    setShowCustomerSuggestions(false)
  }

  // Product search by barcode
  const getProductDetailsByBarcode = (barcode) => {
    if (!barcode) {
      setServerError(undefined)
      return
    }

    getProductDetailsByBarcodeHelper(barcode)
      .then((product) => {
        if (product) {
          setServerError(undefined)
          setSelectedProduct(product)
          setProductBarcode(product.barcode || barcode)
          setDiscountPercentage(product.discount_percentage || 0)

          if (product.inventory && product.inventory.length > 0) {
            const firstBatch = product.inventory[0]
            setSelectedBatch(firstBatch)
            setCostPerUnit(firstBatch.cost_per_unit || 0)
            setRatePerUnit(firstBatch.rate_per_unit || 0)
            setVatPercentage(firstBatch.vat || 0)
          }
        }
      })
      .catch((err) => {
        setServerError(err || 'Product not found')
        setTimeout(() => setServerError(undefined), 5000)
      })
  }

  const handleProductSearch = (searchTerm) => {
    if (productSearchAbortRef.current) {
      productSearchAbortRef.current.abort()
    }
    if (productSearchTimerRef.current) {
      clearTimeout(productSearchTimerRef.current)
    }

    if (!searchTerm) {
      setProductSearchSuggestions(allProducts)
      return
    }

    productSearchTimerRef.current = setTimeout(() => {
      const abortController = new AbortController()
      productSearchAbortRef.current = abortController

      getProductSearchSuggestionsHelper(searchTerm, abortController.signal)
        .then((suggestions) => {
          if (abortController.signal.aborted) return
          setProductSearchSuggestions(suggestions || [])
        })
        .catch(() => {
          if (!abortController.signal.aborted) {
            setProductSearchSuggestions(allProducts)
          }
        })
    }, 500)
  }

  const handleProductSelect = (option) => {
    // If user clicked X to clear, reset product fields
    if (!option) {
      handleClearProduct()
      return
    }
    if (!option.id) return

    getProductDetailByIdHelper(option.id)
      .then((product) => {
        if (product) {
          setServerError(undefined)
          setSelectedProduct(product)
          setProductBarcode(product.barcode || '')
          setDiscountPercentage(product.discount_percentage || 0)

          if (product.inventory && product.inventory.length > 0) {
            const firstBatch = product.inventory[0]
            setSelectedBatch(firstBatch)
            setCostPerUnit(firstBatch.cost_per_unit || 0)
            setRatePerUnit(firstBatch.rate_per_unit || 0)
            setVatPercentage(firstBatch.vat || 0)
          }
        }
      })
      .catch((err) => {
        setServerError('Product not found')
        setTimeout(() => setServerError(undefined), 5000)
      })
  }

  // Barcode scanner
  const handleScan = (result) => {
    if (result) {
      setProductBarcode(result)
      getProductDetailsByBarcode(result)
      setIsScannerVisible(false)
    }
  }

  // Update CPU, RPU and VAT when batch changes
  useEffect(() => {
    if (selectedBatch) {
      setCostPerUnit(selectedBatch.cost_per_unit || 0)
      setRatePerUnit(selectedBatch.rate_per_unit || 0)
      setVatPercentage(selectedBatch.vat || 0)
    }
  }, [selectedBatch])

  // Product-level discount calculations
  useEffect(() => {
    if (quantity && ratePerUnit) {
      const totalPrice = parseFloat(quantity) * parseFloat(ratePerUnit)
      const discAmt = (totalPrice * parseFloat(discountPercentage || 0)) / 100
      setDiscountAmount(parseFloat(discAmt.toFixed(2)))
    }
  }, [discountPercentage, quantity, ratePerUnit])

  // Product-level VAT calculations
  useEffect(() => {
    if (quantity && ratePerUnit) {
      const totalPrice = parseFloat(quantity) * parseFloat(ratePerUnit)
      const afterDiscount = totalPrice - parseFloat(discountAmount || 0)
      const vAmt = (afterDiscount * parseFloat(vatPercentage || 0)) / 100
      setVatAmount(parseFloat(vAmt.toFixed(2)))
    }
  }, [vatPercentage, quantity, ratePerUnit, discountAmount])

  // Add or Update product in table
  const handleAddOrUpdateProduct = () => {
    if (!selectedProduct || !quantity || !selectedBatch) {
      setIsFieldErrorVisible(true)
      return
    }

    // Validate stock
    if (parseInt(quantity) > parseInt(selectedBatch.stock || 0)) {
      setServerError('Quantity exceeds available stock')
      return
    }

    const productData = {
      id:
        selectedTableRowIndex !== null
          ? productsInTable[selectedTableRowIndex].id
          : `product_${Date.now()}`,
      productDetails: selectedProduct,
      batchDetails: selectedBatch,
      barcode: productBarcode,
      productName: selectedProduct.name || selectedProduct.product_name,
      dose: selectedProduct.dose || '',
      category: selectedProduct.category || '',
      batchNumber: selectedBatch.batch_number,
      expiryDate: selectedBatch.expiry_date,
      quantity: parseInt(quantity),
      costPerUnit: parseFloat(costPerUnit),
      ratePerUnit: parseFloat(ratePerUnit),
      discountPercentage: parseFloat(discountPercentage),
      discountAmount: parseFloat(discountAmount),
      vatPercentage: parseFloat(vatPercentage),
      vatAmount: parseFloat(vatAmount),
      totalAmount: parseFloat(quantity * ratePerUnit - discountAmount),
      product_id: selectedProduct.product_id || selectedProduct.id,
      product_batch_inventory_id:
        selectedBatch.product_batch_inventory_id || selectedBatch.id,
      rxMode: selectedBatch?.is_prescription_required || selectedProduct?.is_prescription_required ? 'Rx' : 'OTC',
    }

    if (selectedTableRowIndex !== null) {
      const updatedProducts = [...productsInTable]
      updatedProducts[selectedTableRowIndex] = productData
      setProductsInTable(updatedProducts)
      setSelectedTableRowIndex(null)
    } else {
      setProductsInTable([...productsInTable, productData])
    }

    handleClearProduct()
  }

  const handleClearCustomer = () => {
    setCustomerPhone('')
    setCustomerName('')
    setCustomerAddress('')
    setSelectedCustomer(null)
    setCustomerSuggestions([])
    setShowCustomerSuggestions(false)
  }

  const handleClearProduct = () => {
    setSelectedProduct(null)
    setProductBarcode('')
    setSelectedBatch(null)
    setQuantity('')
    setCostPerUnit(0)
    setRatePerUnit(0)
    setDiscountPercentage(0)
    setDiscountAmount(0)
    setVatPercentage(0)
    setVatAmount(0)
    setSelectedTableRowIndex(null)
    setIsFieldErrorVisible(false)
    setServerError(undefined)
  }

  const handleDeleteProduct = () => {
    if (selectedTableRowIndex !== null) {
      const updatedProducts = [...productsInTable]
      updatedProducts.splice(selectedTableRowIndex, 1)
      setProductsInTable(updatedProducts)
      setSelectedTableRowIndex(null)
      setDeleteProductPopup(false)
      handleClearProduct()
    }
  }

  // Table row click handlers
  const handleTableRowClick = (index) => {
    setSelectedTableRowIndex(index)
  }

  const handleTableRowDoubleClick = (index) => {
    const product = productsInTable[index]
    setSelectedTableRowIndex(index)

    setSelectedProduct(product.productDetails)
    setProductBarcode(product.barcode)
    setSelectedBatch(product.batchDetails)
    setQuantity(product.quantity.toString())
    setCostPerUnit(product.costPerUnit || 0)
    setRatePerUnit(product.ratePerUnit)
    setDiscountPercentage(product.discountPercentage)
    setDiscountAmount(product.discountAmount)
    setVatPercentage(product.vatPercentage)
    setVatAmount(product.vatAmount)
  }

  // Invoice summary calculations
  useEffect(() => {
    let qty = 0
    let total = 0
    let totalVat = 0

    productsInTable.forEach((product) => {
      const baseAmount = product.quantity * product.ratePerUnit
      qty += product.quantity
      total += baseAmount
      totalVat += product.vatAmount || 0
    })

    setTotalQty(qty)
    setTotalItems(productsInTable.length)
    setTotalAmount(parseFloat(total.toFixed(2)))
    setTotalVAT(parseFloat(totalVat.toFixed(2)))

    // Calculate bill-level discount from tableDiscountPercentage (not from product discounts)
    const billDiscountAmt = (total * tableDiscountPercentage) / 100
    setTableDiscountAmount(parseFloat(billDiscountAmt.toFixed(2)))

    const sub = total - billDiscountAmt
    setSubTotal(parseFloat(sub.toFixed(2)))
    setNetAmount(parseFloat((sub + totalVat).toFixed(2)))
  }, [productsInTable, tableDiscountPercentage])

  // Calculate Return Amount based on payment mode and total payable
  useEffect(() => {
    let amountToPay = netAmount

    // For Card, Online, and Cash & Card - use total payable (includes transaction fee)
    if ((paymentMode === 'CARD' || paymentMode === 'ONLINE' || paymentMode === 'CASH_CARD') && transactionFeeAmount > 0) {
      amountToPay = totalPayable
    }

    const returnAmt = parseFloat(paidAmount || 0) - amountToPay
    setReturnAmount(parseFloat(returnAmt.toFixed(2)))
  }, [paidAmount, netAmount, paymentMode, transactionFeeAmount, totalPayable])

  // Table-level discount - only affects bill summary, not product list table
  const handleTableDiscountPercentageChange = (discountPercent) => {
    setTableDiscountPercentage(discountPercent)
    // useEffect will recalculate discount amount, sub total, and net amount
  }

  // Payment mode - Cash & Card auto-calculation
  useEffect(() => {
    if (paymentMode === 'CASH_CARD' && cashAmount) {
      const cardAmt = parseFloat(netAmount) - parseFloat(cashAmount)
      setCardAmount(parseFloat(cardAmt.toFixed(2)))
    }
  }, [cashAmount, netAmount, paymentMode])

  // Transaction fee calculation based on payment mode
  useEffect(() => {
    let feePercent = 0
    let feeAmount = 0
    let baseAmount = netAmount

    if (paymentMode === 'CARD' && selectedPOS && selectedCardType) {
      // Card payment - fee on total net amount
      if (selectedCardType === 'SAME_BANK') {
        feePercent = parseFloat(selectedPOS.same_bank_transaction_fees || 0)
      } else if (selectedCardType === 'OTHER_BANK') {
        feePercent = parseFloat(selectedPOS.other_bank_transaction_fees || 0)
      }
      feeAmount = (baseAmount * feePercent) / 100
    } else if (paymentMode === 'ONLINE' && selectedMFS) {
      // Online payment - fee on total net amount
      feePercent = parseFloat(selectedMFS.transaction_charge || 0)
      feeAmount = (baseAmount * feePercent) / 100
    } else if (paymentMode === 'CASH_CARD' && selectedPOS && selectedCardType && cardAmount > 0) {
      // Cash & Card - fee only on card amount (remaining after cash)
      baseAmount = cardAmount
      if (selectedCardType === 'SAME_BANK') {
        feePercent = parseFloat(selectedPOS.same_bank_transaction_fees || 0)
      } else if (selectedCardType === 'OTHER_BANK') {
        feePercent = parseFloat(selectedPOS.other_bank_transaction_fees || 0)
      }
      feeAmount = (baseAmount * feePercent) / 100
    }

    setTransactionFeePercent(feePercent)
    setTransactionFeeAmount(parseFloat(feeAmount.toFixed(2)))
    setTotalPayable(parseFloat((netAmount + feeAmount).toFixed(2)))
  }, [paymentMode, selectedPOS, selectedCardType, selectedMFS, netAmount, cardAmount])

  // Reset payment-related states when payment mode changes
  useEffect(() => {
    if (paymentMode !== 'CARD' && paymentMode !== 'CASH_CARD') {
      setSelectedPOS(null)
      setSelectedCardType(null)
    }
    if (paymentMode !== 'ONLINE') {
      setSelectedMFS(null)
    }
    if (paymentMode !== 'CASH_CARD') {
      setCashAmount(0)
      setCardAmount(0)
    }
  }, [paymentMode])

  // Helper function to check if we're in the product details section
  const isInProductDetailsSection = (element) => {
    return element.closest('.sales-form-section')
  }

  // Helper function to move focus to next input field
  const moveToNextField = (currentElement) => {
    const form = currentElement.form || currentElement.closest('form')
    if (!form) return false

    // Get all focusable form elements in the product details section only
    const productSection = currentElement.closest('.sales-form-section')
    if (!productSection) return false

    const allInputs = Array.from(
      productSection.querySelectorAll(
        'input:not([disabled]):not([type="checkbox"]):not([type="button"]), select:not([disabled]), textarea:not([disabled])'
      )
    )

    const currentIndex = allInputs.indexOf(currentElement)
    if (currentIndex === -1) return false

    const nextElement = allInputs[currentIndex + 1]
    if (nextElement) {
      nextElement.focus()
      return true
    }

    return false // No next element found, we're at the end
  }

  // Keyboard shortcuts - Enter to move to next field or trigger Add/Update
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault()

      // Try to move to next field
      const movedToNext = moveToNextField(e.target)

      // If we couldn't move to next field (we're at the last field), trigger Add/Update
      if (!movedToNext && isInProductDetailsSection(e.target)) {
        handleAddOrUpdateProduct()
      }
    }

    if (e.key === 'Delete' && selectedTableRowIndex !== null) {
      e.preventDefault()
      setDeleteProductPopup(true)
    }
  }

  // Handle Enter key for react-select components
  const handleSelectKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Find the hidden input that react-select uses
      const selectContainer = e.target.closest('.reactSelect-compact')
      if (selectContainer) {
        const hiddenInput = selectContainer.querySelector(
          'input[type="hidden"]'
        )
        if (hiddenInput) {
          const movedToNext = moveToNextField(hiddenInput)
          if (!movedToNext) {
            handleAddOrUpdateProduct()
          }
        }
      }
    }
  }

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      getProductDetailsByBarcode(productBarcode)
      // Move to next field after looking up product
      moveToNextField(e.target)
    }
  }

  // Calculate weighted average VAT percentage from products
  const getWeightedVatPercentage = () => {
    if (productsInTable.length === 0 || totalAmount === 0) return 0

    let totalVatPercentageWeighted = 0
    productsInTable.forEach((p) => {
      const productAmount = p.quantity * p.ratePerUnit
      totalVatPercentageWeighted += (p.vatPercentage || 0) * productAmount
    })

    const weightedAvgVat = totalVatPercentageWeighted / totalAmount
    return parseFloat(weightedAvgVat.toFixed(2))
  }

  // Prepare invoice data
  const prepareInvoiceData = (status) => {
    return {
      customerDetails: {
        id: selectedCustomer?.id,
        name: customerName,
        mobileNumber: customerCountryCode + customerPhone,
        billingAddress: customerAddress,
        shippingAddress: customerAddress,
      },
      productDetails: productsInTable.map((p) => ({
        product_id:
          p.product_id || p.productDetails?.product_id || p.productDetails?.id,
        product_batch_inventory_id:
          p.product_batch_inventory_id ||
          p.batchDetails?.product_batch_inventory_id ||
          p.batchDetails?.id,
        quantity: p.quantity,
        discount_percentage: p.discountPercentage,
        vat: p.vatAmount,
        vat_percentage: p.vatPercentage,
        rate_per_unit: p.ratePerUnit,
        final_price: p.totalAmount,
        dose: p.dose,
        selected_batch_details: {
          rate_per_unit: p.ratePerUnit,
          cost_per_unit: p.costPerUnit,
          batch_number: p.batchNumber,
          expiry_date: p.expiryDate,
          stock: p.batchDetails?.stock,
        },
      })),
      paymentDetails: {
        amount: totalAmount,
        discount: tableDiscountPercentage,
        vat: totalVAT,
        vat_percentage: getWeightedVatPercentage(),
        total_amount: netAmount,
        payment_mode: paymentMode,
        amount_cash: paymentMode === 'CASH_CARD' ? cashAmount : null,
        amount_card: paymentMode === 'CASH_CARD' ? cardAmount : (paymentMode === 'CARD' ? netAmount : null),
        notes: notes,
        // POS details for Card and Cash & Card payments
        pos_id: (paymentMode === 'CARD' || paymentMode === 'CASH_CARD') && selectedPOS ? selectedPOS.id : null,
        pos_account_name: (paymentMode === 'CARD' || paymentMode === 'CASH_CARD') && selectedPOS ? selectedPOS.pos_account_name : null,
        card_type: (paymentMode === 'CARD' || paymentMode === 'CASH_CARD') && selectedCardType ? selectedCardType : null,
        // MFS details for Online payments
        mfs_id: paymentMode === 'ONLINE' && selectedMFS ? selectedMFS.id : null,
        mfs_name: paymentMode === 'ONLINE' && selectedMFS ? selectedMFS.mfs_name : null,
        // Transaction fees
        transaction_fee_percent: transactionFeePercent > 0 ? transactionFeePercent : null,
        transaction_fee_amount: transactionFeeAmount > 0 ? transactionFeeAmount : null,
        // Final card amount (card amount + fee)
        final_card_amount: paymentMode === 'CARD' && transactionFeeAmount > 0 
          ? netAmount + transactionFeeAmount 
          : paymentMode === 'CASH_CARD' && cardAmount > 0 && transactionFeeAmount > 0 
            ? cardAmount + transactionFeeAmount 
            : null,
        total_payable: transactionFeeAmount > 0 ? totalPayable : netAmount,
      },
      status: status,
    }
  }

  const handleSave = async () => {
    // Validate products
    if (productsInTable.length === 0) {
      setValidationError('Please add at least one product.')
      return
    }

    // Validate customer details
    if (!customerName || !customerPhone) {
      setValidationError('Please enter customer details.')
      return
    }

    // Validate return amount is not negative
    if (returnAmount < 0) {
      setValidationError(
        'Return amount cannot be negative. Please check the paid amount.'
      )
      return
    }

    // Validate paid amount based on payment mode
    if (paymentMode === 'CASH_CARD') {
      // For split payment, validate cash amount
      if (!cashAmount || cashAmount <= 0) {
        setValidationError('Please enter cash amount for split payment.')
        return
      }
    } else if (paymentMode !== 'DUE_SALES') {
      // For other payment modes (CASH, CARD, ONLINE), paid amount is required
      if (!paidAmount || parseFloat(paidAmount) <= 0) {
        setValidationError('Please enter paid amount before saving.')
        return
      }
    }

    setLoading(true)
    setServerError(undefined)

    // Create customer if doesn't exist
    let customerId = selectedCustomer?.id
    if (!customerId) {
      const customerData = {
        name: customerName,
        mobileNumber: customerCountryCode + customerPhone,
        shippingAddress: customerAddress,
        billingAddress: customerAddress,
      }
      addCustomerHelper(customerData)
        .then((newCustomer) => {
          customerId = newCustomer.id
          setSelectedCustomer({ id: newCustomer.id })
        })
        .catch(() => {})
    }

    const data = prepareInvoiceData('COMPLETED')

    const savePromise = isEditMode
      ? updateSalesHelper({ ...data, id: id })
      : createSalesHelper(data)

    savePromise
      .then(async (result) => {
        dispatch(
          isEditMode
            ? updateSalesAlertAction(true)
            : createSalesAlertAction(true)
        )
        setTimeout(() => {
          dispatch(
            isEditMode
              ? updateSalesAlertAction(false)
              : createSalesAlertAction(false)
          )
        }, 5000)

        // Print invoice if checkbox is checked
        if (shouldPrint && result?.id) {
          try {
            await handlePrintInvoice(result.id, loginData?.name, loginData?.pharmacy_name)
            console.log('Invoice printed successfully')
          } catch (err) {
            console.error('Print error:', err)
          }
        }

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to save sales order')
        setLoading(false)
      })
  }

  const handleHold = async () => {
    if (productsInTable.length === 0) {
      setServerError('Please add at least one product.')
      return
    }

    setLoading(true)
    setServerError(undefined)

    // Create customer if doesn't exist
    if (!selectedCustomer?.id && customerName && customerPhone) {
      const customerData = {
        name: customerName || 'Guest',
        mobileNumber: customerCountryCode + (customerPhone || '0000000000'),
        shippingAddress: customerAddress || '',
        billingAddress: customerAddress || '',
      }
      addCustomerHelper(customerData)
        .then((newCustomer) => {
          setSelectedCustomer({ id: newCustomer.id })
        })
        .catch(() => {})
    }

    const data = prepareInvoiceData('HOLD')

    createSalesHelper(data)
      .then(() => {
        dispatch(holdSalesAlertAction(true))
        setTimeout(() => {
          dispatch(holdSalesAlertAction(false))
        }, 5000)

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setServerError(err || 'Failed to hold sales order')
        setLoading(false)
      })
  }

  const handleClose = () => {
    if (productsInTable.length > 0 && !isEditMode) {
      setShowCloseConfirmation(true)
    } else {
      clearDraft(DRAFT_KEY)
      navigate('/home/details')
    }
  }

  const handleConfirmClose = () => {
    clearDraft(DRAFT_KEY)
    navigate('/home/details')
  }

  const handleCancelClose = () => {
    setShowCloseConfirmation(false)
  }

  const handleCancelSale = () => {
    setShowCancelConfirmation(true)
  }

  const handleConfirmCancelSale = async () => {
    setLoading(true)
    setShowCancelConfirmation(false)
    setServerError(undefined)

    cancelSalesHelper(id)
      .then(() => {
        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setServerError(err || 'Failed to cancel sales order')
        setLoading(false)
      })
  }

  const handleCancelCancelSale = () => {
    setShowCancelConfirmation(false)
  }

  // Online Order Handlers
  const handleConfirmOnlineOrder = () => {
    if (productsInTable.length === 0) {
      setValidationError('Please add at least one product before confirming.')
      return
    }
    setShowConfirmPopup(true)
  }

  const handleConfirmOnlineOrderAction = async () => {
    setLoading(true)
    setShowConfirmPopup(false)
    setServerError(undefined)

    // For prescription orders, update products first
    if (isPrescriptionOrder && onlineOrderStatus === 'PRESCRIPTION_PENDING_ON_STORE') {
      const updateData = {
        productDetails: productsInTable.map(product => ({
          product_batch_inventory_id: product.product_batch_inventory_id || 
            product.batchDetails?.product_batch_inventory_id || 
            product.batchDetails?.id ||
            product.productDetails?.inventory?.id,
          quantity: product.quantity,
          discount_percentage: product.discountPercentage,
          vat: product.vatPercentage,
          selected_batch_details: {
            rate_per_unit: product.ratePerUnit,
            cost_per_unit: product.costPerUnit,
            batch_number: product.batchNumber,
            expiry_date: product.expiryDate,
          },
        })),
        paymentDetails: {
          payment_mode: paymentMode,
          discount: tableDiscountPercentage,
          amount: totalAmount,
          total_amount: netAmount,
          vat: totalVAT,
        },
        orderId: id,
      }

      updatePrescriptionOrderHelper(updateData)
        .then(() => {
          // After updating, confirm the order
          confirmOnlineOrder()
        })
        .catch((err) => {
          setValidationError(err || 'Failed to update prescription order')
          setLoading(false)
        })
    } else {
      // For normal online orders, just confirm
      confirmOnlineOrder()
    }
  }

  const confirmOnlineOrder = () => {
    const data = {
      id: id,
      deliveryPartnerId: null, // Will use third-party delivery
      status: SALES_TYPE.CONFIRMED,
    }

    confirmOnlineSalesOrderHelper(data)
      .then(() => {
        dispatch(updateSalesAlertAction(true))
        setTimeout(() => {
          dispatch(updateSalesAlertAction(false))
        }, 5000)

        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to confirm online order')
        setLoading(false)
      })
  }

  const handleCancelOnlineOrder = () => {
    setShowCancelOrderPopup(true)
  }

  const handleConfirmCancelOnlineOrder = async () => {
    setLoading(true)
    setShowCancelOrderPopup(false)
    setServerError(undefined)

    const data = {
      id: id,
      deliveryPartnerId: null,
      status: SALES_TYPE.CANCELLED,
    }

    confirmOnlineSalesOrderHelper(data)
      .then(() => {
        clearDraft(DRAFT_KEY)
        setLoading(false)
        navigate('/home/details')
      })
      .catch((err) => {
        setValidationError(err || 'Failed to cancel online order')
        setLoading(false)
      })
  }

  const handleCancelCancelOnlineOrder = () => {
    setShowCancelOrderPopup(false)
  }

  const formatProductName = (product) => {
    let name = product.productName
    if (product.dose) {
      name += ` (${product.dose})`
    }
    if (product.strength) {
      name += `-${product.strength}`
    }
    // Category is now in a separate column
    return name
  }

  // Format product label: Name (dose)-Strength-Category (matching Product Receiving format)
  const formatProductLabel = (product) => {
    if (!product) return ''

    const name = product.name || product.product_name || product.label || ''
    const dose = product.dose || ''
    const strength = product.strength || ''
    const category = typeof product.category === 'string'
      ? product.category
      : product.category?.label || product.category?.value || ''

    return `${name}${dose ? ` (${dose})` : ''}${strength ? `-${strength}` : ''}${category ? `-${category}` : ''}`
  }

  return (
    <div className="containerHome">
      {!isMobile && <Header />}
      <SideMenu activeItem={SIDEBAR_CONST.HOME} component="HOME_DETAILS" />
      {isMobile && (
        <MobileHeader
          handleBack={handleClose}
          title={isEditMode ? 'Edit Sales' : 'New Sales'}
        />
      )}
      <div className="pageBody dFlex">
        {!isMobile && (
          <div className="pageBodyLeftPanel sticky">
            <div className="chainNavigationWrapper">
              <ul className="chainNavigation">
                {HOME_NAVIGATION_ITEMS.map((item) => {
                  const feature = permissions?.find(
                    (p) => p.feature === item.feature
                  )
                  if (feature?.type !== PERMISSION_TYPE.RESTRICTED) {
                    return (
                      <li
                        key={item.name}
                        className={`chainItem ${
                          item.name === HOME_CONST.SALES ? 'active' : ''
                        }`}
                        onClick={() => {
                          dispatch(activeHomeDetailNavigation(item.name))
                          navigate('/home/details')
                        }}
                      >
                        {item.homeDetailIcon}
                        <span>{item.name}</span>
                      </li>
                    )
                  }
                  return null
                })}
              </ul>
            </div>
          </div>
        )}
        <div className="pageBodyRightPanel sales-form-container">
          <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiCenter mb-4">
            <div className="pageBodyRightPanelHeading">
              {isEditMode ? 'Edit Sales' : 'New Sales'}
            </div>
            <button className="btn btnSecondary" onClick={handleClose}>
              Close
            </button>
          </div>

          <form onKeyDown={handleKeyDown}>
            {/* Online Order Info Banner */}
            {isOnlineOrder && (
              <div className="online-order-banner">
                <div className="banner-content">
                  <span className="banner-badge">Online Order</span>
                  <span className="banner-status">
                    Status: {onlineOrderStatus === 'PRESCRIPTION_PENDING_ON_STORE' 
                      ? 'Prescription Pending' 
                      : onlineOrderStatus === 'CREATED' 
                        ? 'Awaiting Confirmation' 
                        : onlineOrderStatus}
                  </span>
                  {isPrescriptionOrder && (
                    <span className="banner-type">Prescription Order</span>
                  )}
                </div>
              </div>
            )}

            {/* Prescription Images Section */}
            {isOnlineOrder && isPrescriptionOrder && prescriptionFiles.length > 0 && (
              <div className="sales-form-section prescription-section">
                <h3 className="section-title">Prescription Images</h3>
                <div className="prescription-images-grid">
                  {prescriptionFiles.map((file, index) => (
                    <div key={index} className="prescription-image-item">
                      <div 
                        className="prescription-thumbnail"
                        onClick={() => setSelectedPrescriptionImage(file)}
                      >
                        <img 
                          src={Config.getFullImageUrl(file.url)} 
                          alt={file.name || `Prescription ${index + 1}`}
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                        <div className="prescription-placeholder" style={{ display: 'none' }}>
                          Click to view
                        </div>
                      </div>
                      <span className="prescription-label">{file.name || `Prescription ${index + 1}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 1: Customer & Product Details */}
            <div className="sales-form-section">
              <h3 className="section-title">Customer Details</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label>Country</label>
                  <select
                    ref={firstInputRef}
                    value={customerCountryCode}
                    onChange={(e) => setCustomerCountryCode(e.target.value)}
                  >
                    <option value="+880">+880</option>
                    <option value="+91">+91</option>
                    <option value="+1">+1</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <div className="customer-field-wrapper">
                    <input
                      type="text"
                      placeholder="Enter phone"
                      value={customerPhone}
                      onChange={(e) =>
                        handleCustomerPhoneChange(e.target.value)
                      }
                      onKeyDown={handleCustomerInputKeyDown}
                      onFocus={() => {
                        if (customerSuggestions.length > 0)
                          setShowCustomerSuggestions(true)
                      }}
                    />
                    {showCustomerSuggestions &&
                      customerSuggestions.length > 0 && (
                        <div className="customer-suggestions">
                          {customerSuggestions.map((customer, index) => (
                            <div
                              key={customer.id || index}
                              className={`customer-suggestion-item ${
                                index === selectedSuggestionIndex
                                  ? 'highlighted'
                                  : ''
                              }`}
                              onClick={() => handleSelectCustomer(customer)}
                              onMouseEnter={() =>
                                setSelectedSuggestionIndex(index)
                              }
                            >
                              <div className="customer-name">
                                {customer.name}
                              </div>
                              <div className="customer-phone">
                                {customer.mobile_no}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
                <div className="form-field">
                  <label>Name</label>
                  <div className="customer-field-wrapper with-clear">
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={customerName}
                      onChange={(e) => handleCustomerNameChange(e.target.value)}
                      onKeyDown={handleCustomerInputKeyDown}
                      onFocus={() => {
                        if (customerSuggestions.length > 0)
                          setShowCustomerSuggestions(true)
                      }}
                    />
                    {customerName && (
                      <span
                        className="clear-icon"
                        onClick={handleClearCustomer}
                        title="Clear customer details"
                      >
                        ×
                      </span>
                    )}
                    {showCustomerSuggestions &&
                      customerSuggestions.length > 0 && (
                        <div className="customer-suggestions">
                          {customerSuggestions.map((customer, index) => (
                            <div
                              key={customer.id || index}
                              className={`customer-suggestion-item ${
                                index === selectedSuggestionIndex
                                  ? 'highlighted'
                                  : ''
                              }`}
                              onClick={() => handleSelectCustomer(customer)}
                              onMouseEnter={() =>
                                setSelectedSuggestionIndex(index)
                              }
                            >
                              <div className="customer-name">
                                {customer.name}
                              </div>
                              <div className="customer-phone">
                                {customer.mobile_no}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
                <div className="form-field">
                  <label>Address</label>
                  <input
                    type="text"
                    placeholder="Enter address"
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>

              <h3 className="section-title mt-3">Product Details</h3>
              <div className="form-grid">
                <div className="form-field with-icon">
                  <label>Barcode/QR</label>
                  <input
                    type="text"
                    placeholder="Scan or enter barcode"
                    value={productBarcode}
                    ref={barcodeInputRef}
                    onChange={(e) => setProductBarcode(e.target.value)}
                    onKeyDown={handleBarcodeKeyDown}
                    onBlur={() => getProductDetailsByBarcode(productBarcode)}
                  />
                  <div
                    className="field-icon"
                    onClick={() => setIsScannerVisible(!isScannerVisible)}
                  >
                    <BarcodeIcon />
                  </div>
                  {isScannerVisible && <BarcodeScanner onScan={handleScan} />}
                </div>

                <div className="form-field">
                  <label>Product</label>
                  <Select
                    className="reactSelect-compact"
                    value={
                      selectedProduct
                        ? {
                            label: formatProductLabel(selectedProduct),
                            value: selectedProduct.id,
                            id: selectedProduct.id,
                          }
                        : null
                    }
                    onInputChange={handleProductSearch}
                    onChange={(option) => handleProductSelect(option)}
                    onMenuOpen={() => {
                      if (productSearchSuggestions.length === 0) {
                        setProductSearchSuggestions(allProducts)
                      }
                    }}
                    options={productSearchSuggestions.map((p) => ({
                      label: formatProductLabel(p),
                      value: p.id,
                      id: p.id,
                    }))}
                    placeholder="Select..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        minHeight: '36px',
                        maxHeight: '36px',
                        borderColor: '#d4d4d4',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '2px 8px',
                        maxHeight: '32px',
                        overflow: 'hidden',
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      option: (base) => ({
                        ...base,
                        fontSize: '13px',
                        padding: '8px 12px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }),
                      menu: (base) => ({
                        ...base,
                        width: 'max-content',
                        minWidth: '100%',
                      }),
                    }}
                  />
                </div>

                <div className="form-field">
                  <label>Batch</label>
                  <Select
                    className="reactSelect-compact"
                    value={selectedBatch}
                    onChange={setSelectedBatch}
                    options={selectedProduct?.inventory || []}
                    getOptionLabel={(option) =>
                      `${option.batch_number}, Exp: ${formatDate(option.expiry_date)}, Qty: ${option.stock || 0}`
                    }
                    getOptionValue={(option) =>
                      option.product_batch_inventory_id || option.id
                    }
                    placeholder="Select..."
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        minHeight: '36px',
                        borderColor: '#d4d4d4',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '2px 8px',
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      option: (base) => ({
                        ...base,
                        fontSize: '13px',
                        padding: '8px 12px',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: '13px',
                      }),
                    }}
                  />
                </div>

                <div className="form-field">
                  <label>Quantity</label>
                  <input
                    type="number"
                    placeholder="Qty"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {selectedBatch && (
                    <span className="available-stock">
                      Avail: {selectedBatch.stock}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-grid-6 mt-2">
                <div className="form-field">
                  <label>CPU</label>
                  <input
                    type="number"
                    value={costPerUnit}
                    onChange={(e) => setCostPerUnit(e.target.value)}
                    disabled
                  />
                </div>

                <div className="form-field">
                  <label>Disc %</label>
                  <input
                    type="number"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                <div className="form-field">
                  <label>Disc Amt</label>
                  <input
                    type="number"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                <div className="form-field">
                  <label>VAT %</label>
                  <input
                    type="number"
                    value={vatPercentage}
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-field">
                  <label>VAT Amt</label>
                  <input
                    type="number"
                    value={vatAmount}
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-field">
                  <label>RPU</label>
                  <input
                    type="number"
                    value={ratePerUnit}
                    disabled
                    className="disabled-input"
                  />
                </div>
              </div>

              <div className="form-actions-right mt-2">
                <button
                  type="button"
                  onClick={handleAddOrUpdateProduct}
                  className="btn btn-primary"
                >
                  {selectedTableRowIndex !== null ? 'UPDATE' : 'ADD'}
                </button>
                <button
                  type="button"
                  onClick={handleClearProduct}
                  className="btn btn-secondary"
                >
                  CLEAR
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteProductPopup(true)}
                  className="btn btn-danger"
                  disabled={selectedTableRowIndex === null}
                >
                  DELETE
                </button>
                <label
                  className="print-checkbox-tooltip"
                  title="Print after save"
                >
                  <input
                    type="checkbox"
                    checked={shouldPrint}
                    onChange={(e) => setShouldPrint(e.target.checked)}
                  />
                </label>
              </div>

              {serverError && (
                <div className="invalidFeedback mt-3">{serverError}</div>
              )}
            </div>

            {/* Section 3: Products Table with Bill Summary */}
            <div className="sales-form-section mt-3">
              <div className="product-list-with-summary">
                <div className="product-list-section">
                  <h3 className="section-title">Product List</h3>
                  {productsInTable.length === 0 ? (
                    <p className="empty-message">
                      No products added yet. Add products using the form above.
                    </p>
                  ) : (
                    <div className="products-table-wrapper">
                      <table className="products-table sales-product-table-fit">
                        <thead>
                          <tr>
                            <th width="3%">SL</th>
                            <th width="18%">Product Name</th>
                            <th width="7%">Category</th>
                            <th width="9%">Batch</th>
                            <th width="9%">Expiry</th>
                            <th width="4%">Qty</th>
                            <th width="5%">VAT %</th>
                            <th width="7%">VAT Amt</th>
                            <th width="5%">Disc %</th>
                            <th width="7%">Total Disc</th>
                            <th width="6%">RPU</th>
                            <th width="8%">Amount</th>
                            <th width="6%">Rx Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productsInTable.map((product, index) => (
                            <tr
                              key={product.id}
                              onClick={() => handleTableRowClick(index)}
                              onDoubleClick={() =>
                                handleTableRowDoubleClick(index)
                              }
                              className={
                                selectedTableRowIndex === index
                                  ? 'selected'
                                  : ''
                              }
                              title="Double-click to edit"
                            >
                              <td>{index + 1}</td>
                              <td>{formatProductName(product)}</td>
                              <td>{product.category || '-'}</td>
                              <td>{product.batchNumber}</td>
                              <td>{formatDate(product.expiryDate)}</td>
                              <td>{product.quantity}</td>
                              <td>{product.vatPercentage || 0}</td>
                              <td>{product.vatAmount?.toFixed(2) || '0.00'}</td>
                              <td>{product.discountPercentage}</td>
                              <td>{product.discountAmount?.toFixed(2)}</td>
                              <td>{product.ratePerUnit?.toFixed(2)}</td>
                              <td>{product.totalAmount?.toFixed(2)}</td>
                              <td>{product.rxMode || 'OTC'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Bill Summary */}
                <div className="bill-summary-compact">
                  <h3 className="section-title">Bill Summary</h3>
                  <div className="summary-header-grid">
                    <div className="summary-box">
                      <div className="summary-box-label">Total Qty</div>
                      <div className="summary-box-value">{totalQty}</div>
                    </div>
                    <div className="summary-box">
                      <div className="summary-box-label">Total Items</div>
                      <div className="summary-box-value">{totalItems}</div>
                    </div>
                  </div>
                  <table className="summary-table">
                    <tbody>
                      <tr>
                        <td>Total Amt</td>
                        <td className="text-right">{totalAmount.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>Discount %</td>
                        <td className="text-right">
                          <input
                            type="number"
                            value={tableDiscountPercentage}
                            onChange={(e) =>
                              handleTableDiscountPercentageChange(
                                parseFloat(e.target.value || 0)
                              )
                            }
                            onKeyDown={handleKeyDown}
                            className="summary-input"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Disc Amt</td>
                        <td className="text-right">
                          {tableDiscountAmount.toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td>Sub Total</td>
                        <td className="text-right">{subTotal.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>VAT</td>
                        <td className="text-right">{totalVAT.toFixed(2)}</td>
                      </tr>
                      <tr className="highlight-row">
                        <td className="highlight">Net Amt</td>
                        <td className="text-right highlight">
                          {netAmount.toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td>Pay Mode</td>
                        <td>
                          <Select
                            className="summary-select"
                            value={PAYMENT_MODE_OPTIONS.find(
                              (opt) => opt.name === paymentMode
                            )}
                            onChange={(option) => setPaymentMode(option.name)}
                            options={PAYMENT_MODE_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.name}
                            placeholder="Select..."
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({
                                ...base,
                                minHeight: '32px',
                                fontSize: '12px',
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                              }),
                            }}
                          />
                        </td>
                      </tr>
                      {/* Card Payment - Select Bank and Card Type */}
                      {paymentMode === 'CARD' && (
                        <>
                          <tr>
                            <td>Select Bank</td>
                            <td>
                              {posList.length > 0 ? (
                                <Select
                                  className="summary-select"
                                  value={selectedPOS}
                                  onChange={(option) => {
                                    setSelectedPOS(option)
                                    setSelectedCardType(null) // Reset card type when bank changes
                                  }}
                                  options={posList}
                                  getOptionLabel={(option) => option.pos_account_name}
                                  getOptionValue={(option) => option.id}
                                  placeholder="Select Bank..."
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '32px',
                                      fontSize: '12px',
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: '0 6px',
                                    }),
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#d9534f', fontSize: '11px' }}>
                                  No POS configured. Add in Settings.
                                </span>
                              )}
                            </td>
                          </tr>
                          {selectedPOS && (
                            <tr>
                              <td>Card Type</td>
                              <td>
                                <Select
                                  className="summary-select"
                                  value={CARD_TYPE_OPTIONS.find(opt => opt.name === selectedCardType)}
                                  onChange={(option) => setSelectedCardType(option?.name || null)}
                                  options={CARD_TYPE_OPTIONS}
                                  getOptionLabel={(option) => option.label}
                                  getOptionValue={(option) => option.name}
                                  placeholder="Select Type..."
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '32px',
                                      fontSize: '12px',
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: '0 6px',
                                    }),
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                          {selectedPOS && selectedCardType && transactionFeePercent > 0 && (
                            <>
                              <tr>
                                <td>Fee ({transactionFeePercent}%)</td>
                                <td className="text-right">{transactionFeeAmount.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td>Final Card Amt</td>
                                <td className="text-right">{(netAmount + transactionFeeAmount).toFixed(2)}</td>
                              </tr>
                            </>
                          )}
                        </>
                      )}
                      {/* Online Payment - Select MFS */}
                      {paymentMode === 'ONLINE' && (
                        <>
                          <tr>
                            <td>Select Type</td>
                            <td>
                              {mfsList.length > 0 ? (
                                <Select
                                  className="summary-select"
                                  value={selectedMFS}
                                  onChange={(option) => setSelectedMFS(option)}
                                  options={mfsList}
                                  getOptionLabel={(option) => option.mfs_name}
                                  getOptionValue={(option) => option.id}
                                  placeholder="Select MFS..."
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '32px',
                                      fontSize: '12px',
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: '0 6px',
                                    }),
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#d9534f', fontSize: '11px' }}>
                                  No MFS configured. Add in Settings.
                                </span>
                              )}
                            </td>
                          </tr>
                          {selectedMFS && transactionFeePercent > 0 && (
                            <tr>
                              <td>Fee ({transactionFeePercent}%)</td>
                              <td className="text-right">{transactionFeeAmount.toFixed(2)}</td>
                            </tr>
                          )}
                        </>
                      )}
                      {/* Cash & Card Payment */}
                      {paymentMode === 'CASH_CARD' && (
                        <>
                          <tr>
                            <td>Cash Amt</td>
                            <td>
                              <input
                                type="number"
                                value={cashAmount}
                                onChange={(e) =>
                                  setCashAmount(parseFloat(e.target.value || 0))
                                }
                                onKeyDown={handleKeyDown}
                                className="summary-input"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>Card Amt</td>
                            <td className="text-right">
                              {cardAmount.toFixed(2)}
                            </td>
                          </tr>
                          {cardAmount > 0 && (
                            <>
                              <tr>
                                <td>Select Bank</td>
                                <td>
                                  {posList.length > 0 ? (
                                    <Select
                                      className="summary-select"
                                      value={selectedPOS}
                                      onChange={(option) => {
                                        setSelectedPOS(option)
                                        setSelectedCardType(null)
                                      }}
                                      options={posList}
                                      getOptionLabel={(option) => option.pos_account_name}
                                      getOptionValue={(option) => option.id}
                                      placeholder="Select Bank..."
                                      isClearable
                                      menuPortalTarget={document.body}
                                      styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                          ...base,
                                          minHeight: '32px',
                                          fontSize: '12px',
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          padding: '0 6px',
                                        }),
                                      }}
                                    />
                                  ) : (
                                    <span style={{ color: '#d9534f', fontSize: '11px' }}>
                                      No POS configured. Add in Settings.
                                    </span>
                                  )}
                                </td>
                              </tr>
                              {selectedPOS && (
                                <tr>
                                  <td>Card Type</td>
                                  <td>
                                    <Select
                                      className="summary-select"
                                      value={CARD_TYPE_OPTIONS.find(opt => opt.name === selectedCardType)}
                                      onChange={(option) => setSelectedCardType(option?.name || null)}
                                      options={CARD_TYPE_OPTIONS}
                                      getOptionLabel={(option) => option.label}
                                      getOptionValue={(option) => option.name}
                                      placeholder="Select Type..."
                                      isClearable
                                      menuPortalTarget={document.body}
                                      styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                          ...base,
                                          minHeight: '32px',
                                          fontSize: '12px',
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          padding: '0 6px',
                                        }),
                                      }}
                                    />
                                  </td>
                                </tr>
                              )}
                              {selectedPOS && selectedCardType && transactionFeePercent > 0 && (
                                <>
                                  <tr>
                                    <td>Fee ({transactionFeePercent}%)</td>
                                    <td className="text-right">{transactionFeeAmount.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td>Final Card Amt</td>
                                    <td className="text-right">{(cardAmount + transactionFeeAmount).toFixed(2)}</td>
                                  </tr>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {/* Total Payable with fees */}
                      {(paymentMode === 'CARD' || paymentMode === 'ONLINE' || paymentMode === 'CASH_CARD') && transactionFeeAmount > 0 && (
                        <tr className="highlight-row">
                          <td className="highlight">Total Payable</td>
                          <td className="text-right highlight">
                            {totalPayable.toFixed(2)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>Paid Amt</td>
                        <td>
                          <input
                            type="number"
                            value={paidAmount}
                            onChange={(e) =>
                              setPaidAmount(
                                e.target.value ? parseFloat(e.target.value) : ''
                              )
                            }
                            onKeyDown={handleKeyDown}
                            className="summary-input"
                            placeholder=""
                          />
                        </td>
                      </tr>
                      <tr className="total-row">
                        <td>Return Amt</td>
                        <td className="text-right highlight">
                          {returnAmount.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Show different button based on order type */}
                  {isOnlineOrder ? (
                    <button
                      type="button"
                      onClick={handleConfirmOnlineOrder}
                      className="btn btn-primary btn-block mt-3"
                      disabled={loading}
                    >
                      {loading ? 'Confirming...' : 'Confirm Order'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSave}
                      className="btn btn-primary btn-block mt-3"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions-bottom">
              {/* For in-store orders */}
              {!isOnlineOrder && !isEditMode && (
                <button
                  type="button"
                  onClick={handleHold}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Hold Invoice'}
                </button>
              )}
              {!isOnlineOrder && isEditMode && (
                <button
                  type="button"
                  onClick={handleCancelSale}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Cancel Sale
                </button>
              )}
              {/* For online orders */}
              {isOnlineOrder && (
                <button
                  type="button"
                  onClick={handleCancelOnlineOrder}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Cancel Order
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      {showCloseConfirmation && (
        <PopupCard
          header="End Sales Process"
          body="Are you sure you want to end the sales process? Any unsaved data will be lost."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={handleCancelClose}
          >
            Cancel
          </button>
          <button className="btn btnDanger" onClick={handleConfirmClose}>
            End Process
          </button>
        </PopupCard>
      )}

      {/* Delete Product Confirmation */}
      {deleteProductPopup && (
        <PopupCard
          header="Delete Product"
          body="Are you sure you want to delete this product from the list?"
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={() => setDeleteProductPopup(false)}
          >
            Cancel
          </button>
          <button className="btn btnDanger" onClick={handleDeleteProduct}>
            Delete
          </button>
        </PopupCard>
      )}

      {/* Cancel Sale Confirmation */}
      {showCancelConfirmation && (
        <PopupCard
          header="Cancel Sale"
          body="Are you sure you want to cancel this sale? This action cannot be undone."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={handleCancelCancelSale}
          >
            No, Keep Sale
          </button>
          <button className="btn btnDanger" onClick={handleConfirmCancelSale}>
            Yes, Cancel Sale
          </button>
        </PopupCard>
      )}

      {/* Validation Error Popup */}
      {validationError && (
        <PopupCard header="Validation Error" body={validationError}>
          <button
            className="btn btnPrimary"
            onClick={() => setValidationError(null)}
          >
            OK
          </button>
        </PopupCard>
      )}

      {/* Confirm Online Order Popup */}
      {showConfirmPopup && (
        <PopupCard
          header="Confirm Online Order"
          body="Are you sure you want to confirm this order? The order will be sent for delivery."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={() => setShowConfirmPopup(false)}
          >
            Cancel
          </button>
          <button className="btn btnPrimary" onClick={handleConfirmOnlineOrderAction}>
            Confirm Order
          </button>
        </PopupCard>
      )}

      {/* Cancel Online Order Popup */}
      {showCancelOrderPopup && (
        <PopupCard
          header="Cancel Online Order"
          body="Are you sure you want to cancel this online order? The customer will be notified."
        >
          <button
            className="btn btnPrimaryOutlined"
            onClick={handleCancelCancelOnlineOrder}
          >
            No, Keep Order
          </button>
          <button className="btn btnDanger" onClick={handleConfirmCancelOnlineOrder}>
            Yes, Cancel Order
          </button>
        </PopupCard>
      )}

      {/* Prescription Image Zoom Popup */}
      {selectedPrescriptionImage && (
        <div className="prescription-modal-overlay" onClick={() => setSelectedPrescriptionImage(null)}>
          <div className="prescription-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="prescription-modal-close"
              onClick={() => setSelectedPrescriptionImage(null)}
            >
              ✕
            </button>
            <div className="prescription-modal-content">
              <img 
                src={Config.getFullImageUrl(selectedPrescriptionImage.url)} 
                alt={selectedPrescriptionImage.name || 'Prescription'}
              />
            </div>
            <div className="prescription-modal-footer">
              <span>{selectedPrescriptionImage.name || 'Prescription Image'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesForm
